import React from 'react'
import { useNavigate } from 'react-router-dom'

import { BaseSafeFormView, BaseText, BaseView } from '@/components/base'
import { StickyHeaderNavigation } from '@/components/StickyHeaderNavigation'
import { useUserStore } from '@/store/useUserStore'
import { responsiveAdapterVertical } from '@/utils/responsive'
import { useTheme } from '@/hooks/useTheme'
import { AppIcons } from '@/components/AppIcons'
import { AppButton } from '@/components'
import { useDeviceShare } from '@/hooks'
import { useGetMessageAndLinkToShareMyProfileWithReferralCode } from '@/hooks/useGetMessageAndLinkToShareMyProfile'
import { useTranslation } from 'react-i18next'
import { useGetFeaturesSettings } from '@/domain/settings'
import { LoadingSpinner } from '@/components/LoadingSpinner'

export const CashbackInfoScreen: React.FC = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const { t } = useTranslation()

  const { data: featureSettings, isLoading } = useGetFeaturesSettings()
  const cashbackProgram = featureSettings?.cashback?.cashback_programs?.[0]
  const isCashbackEnabled = cashbackProgram?.is_enabled !== false

  if (isLoading) {
    return (
      <BaseView flex={1} center>
        <LoadingSpinner />
      </BaseView>
    )
  }

  if (!isCashbackEnabled) {
    navigate(-1)
    return null
  }

  // Dynamic values from API
  const currencyName = cashbackProgram?.cashback_currency_name || cashbackProgram?.name || 'BTC'
  const currencyCode = cashbackProgram?.cashback_currency_code || cashbackProgram?.code || 'BTC'
  const percentage = cashbackProgram?.percentage ?? 0
  const minRate = cashbackProgram?.min_percentage_rate
  const maxRate = cashbackProgram?.max_percentage_rate
  const minThreshold = cashbackProgram?.min_percentage_threshold
  const maxThreshold = cashbackProgram?.max_percentage_threshold
  const paymentCycle = cashbackProgram?.payment_cycle

  const rateDisplay = minRate != null && maxRate != null
    ? `${minRate}% - ${maxRate}%`
    : `${percentage}%`

  const thresholdDisplay = minThreshold != null && maxThreshold != null
    ? `$${minThreshold.toLocaleString()} - $${maxThreshold.toLocaleString()}`
    : null

  const cycleDisplay = paymentCycle
    ? paymentCycle >= 7 ? `${Math.round(paymentCycle / 7)} week(s)` : `${paymentCycle} day(s)`
    : null

  const BulletPoint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <BaseView row gap={12}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="6"
        height="6"
        viewBox="0 0 6 6"
        fill="none"
        className="mt-[10px]"
      >
        <circle cx="3" cy="3" r="3" fill="#84878C" />
      </svg>
      <BaseText fontSize={16} fontFamily="regular">
        {children}
      </BaseText>
    </BaseView>
  )

  return (
    <BaseSafeFormView flex={1} bgColor="background.screen" className="h-full">
      <StickyHeaderNavigation
        onBackPress={() => navigate(-1)}
        showBackground={false}
      />

      <BaseView
        style={{
          flex: 1,
          overflowY: 'auto',
          paddingTop: 38,
          paddingBottom: 20,
          position: 'relative',
          zIndex: 100,
        }}
        gap={32}
      >
        <BaseText
          fontSize={32}
          fontFamily="semibold"
          className="text-left pt-32 pl-3"
        >
          {t('translation:cashbackPage.title')}
        </BaseText>
        <BaseView gap={20} className="items-start justify-start relative pl-3">
          {/* Rates section — driven by API data */}
          <BaseView gap={8} className="items-start justify-start shrink-0 relative">
            <BaseText fontSize={18} fontFamily="semibold" className="block">
              {t('translation:cashbackPage.rates.title')}
            </BaseText>
            <BulletPoint>
              {`Earn ${rateDisplay} cashback in ${currencyName} (${currencyCode}) on every Kem Card purchase`}
            </BulletPoint>
            {thresholdDisplay && (
              <BulletPoint>
                {`Cashback tier based on monthly spend: ${thresholdDisplay}`}
              </BulletPoint>
            )}
            {!thresholdDisplay && (
              <BulletPoint>
                {t('translation:cashbackPage.rates.description2')}
              </BulletPoint>
            )}
          </BaseView>

          {/* Payouts section */}
          <BaseView gap={8} className="items-start justify-start shrink-0 relative">
            <BaseText fontSize={18} fontFamily="semibold" className="block">
              {t('translation:cashbackPage.payouts.title')}
            </BaseText>
            <BulletPoint>
              {`Cashback is paid in ${currencyName} (${currencyCode})`}
            </BulletPoint>
            {cycleDisplay && (
              <BulletPoint>
                {`Payment cycle: every ${cycleDisplay}`}
              </BulletPoint>
            )}
            {!cycleDisplay && (
              <BulletPoint>
                {t('translation:cashbackPage.payouts.description2')}
              </BulletPoint>
            )}
            <BulletPoint>
              {t('translation:cashbackPage.payouts.description3')}
            </BulletPoint>
          </BaseView>

          {/* Tracking section */}
          <BaseView gap={8} className="items-start justify-start shrink-0 relative">
            <BaseText fontSize={18} fontFamily="semibold" className="block">
              {t('translation:cashbackPage.tracking.title')}
            </BaseText>
            <BulletPoint>
              {t('translation:cashbackPage.tracking.description')}
            </BulletPoint>
            <BulletPoint>
              {t('translation:cashbackPage.tracking.description2')}
            </BulletPoint>
          </BaseView>
        </BaseView>
        <AppButton
          label={t('translation:cashbackPage.confirm')}
          type="secondaryGray"
          onPress={() => navigate('/kem-card')}
          style={{ zIndex: 100 }}
        />
      </BaseView>
      <svg
        width="345"
        height="372"
        viewBox="0 0 345 372"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute top-0 right-0 -z-0"
      >
        <g opacity="0.5" filter="url(#filter0_f_38828_19122)">
          <circle cx="415" cy="44" r="128" fill="#E5007A" />
        </g>
        <g opacity="0.5" filter="url(#filter1_f_38828_19122)">
          <circle cx="381.5" cy="-11.5" r="181.5" fill="#3200C9" />
          <circle
            cx="381.5"
            cy="-11.5"
            r="181.5"
            fill="url(#paint0_linear_38828_19122)"
          />
        </g>
        <defs>
          <filter
            id="filter0_f_38828_19122"
            x="87"
            y="-284"
            width="656"
            height="656"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="100"
              result="effect1_foregroundBlur_38828_19122"
            />
          </filter>
          <filter
            id="filter1_f_38828_19122"
            x="0"
            y="-393"
            width="763"
            height="763"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="100"
              result="effect1_foregroundBlur_38828_19122"
            />
          </filter>
          <linearGradient
            id="paint0_linear_38828_19122"
            x1="200"
            y1="152.714"
            x2="585.948"
            y2="115.216"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FF7F15" />
            <stop offset="1" stopColor="#FFD24B" />
          </linearGradient>
        </defs>
      </svg>
      <svg
        width="375"
        height="287"
        viewBox="0 0 375 287"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute bottom-0 left-1/2 -z-0 -translate-x-1/2 w-full"
      >
        <g opacity="0.5" filter="url(#filter0_f_38401_27200)">
          <ellipse
            cx="187.5"
            cy="381.5"
            rx="187.5"
            ry="181.5"
            fill="url(#paint0_linear_38401_27200)"
          />
        </g>
        <defs>
          <filter
            id="filter0_f_38401_27200"
            x="-200"
            y="0"
            width="775"
            height="763"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="100"
              result="effect1_foregroundBlur_38401_27200"
            />
          </filter>
          <linearGradient
            id="paint0_linear_38401_27200"
            x1="-44.5"
            y1="382"
            x2="395.472"
            y2="500.546"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#E5007A" />
            <stop offset="0.236599" stopColor="#FF7F15" />
            <stop offset="1" stopColor="#FFD24B" />
          </linearGradient>
        </defs>
      </svg>
    </BaseSafeFormView>
  )
}
